import { useState, useCallback, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

interface UseDeleteBusinessResult {
  loading: boolean;
  error: string | null;
  deleteBusiness: (businessId: string, apiUrl?: string) => Promise<boolean>;
}

export function useDeleteBusiness(): UseDeleteBusinessResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteBusiness = useCallback(async (businessId: string, apiUrl?: string): Promise<boolean> => {
    const url = apiUrl || process.env.REACT_APP_API_URL;
    if (!url) { setError('REACT_APP_API_URL is not set'); return false; }

    setLoading(true);
    setError(null);

    try {
      let token: string | null = null;
      if (isAuthenticated && accountsRef.current.length > 0) {
        token = await acquireApiToken(instance, accountsRef.current[0]);
      }

      const response = await fetch(`${url}/admin/businesses/${businessId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || `HTTP ${response.status}`);
        return false;
      }

      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, instance]);

  return { loading, error, deleteBusiness };
}
